/**
 * Server-Sent Events read out of a growing text — Milo's answer stream (docs/API.md §14.5),
 * which arrives in the body of a POST, where no `EventSource` reaches.
 *
 * Imports nothing, so `tools/sse.test.mjs` runs it in Node with the types stripped.
 */

export interface ISseEvent {
    /** `event:` — "message" where the block names none. */
    event: string
    /** `data:` lines joined with a line break. */
    data:  string
}

/**
 * The complete events in `buffer`, and what is left of it (a block still arriving).
 * Comments (`: ping`) and blocks without data are dropped; CRLF is read as LF.
 */
export const splitEvents = (buffer: string): { events: ISseEvent[], rest: string } => {
    // A CR at the very end may be the first half of a CRLF split between two pieces: held back.
    const held = buffer.charAt(buffer.length - 1) === '\r' ? '\r' : ''
    const text = (held ? buffer.slice(0, -1) : buffer).replace(/\r\n?/g, '\n')
    const blocks = text.split('\n\n')
    const rest = (blocks.pop() ?? '') + held
    const events: ISseEvent[] = []

    for (const block of blocks) {
        let event = 'message'
        const data: string[] = []

        for (const line of block.split('\n')) {
            if (!line || line.charAt(0) === ':') continue

            const colon = line.indexOf(':')
            const field = colon < 0 ? line : line.slice(0, colon)
            let value = colon < 0 ? '' : line.slice(colon + 1)

            if (value.charAt(0) === ' ') value = value.slice(1)

            if (field === 'event') event = value
            else if (field === 'data') data.push(value)
        }

        if (data.length) events.push({ event, data: data.join('\n') })
    }

    return { events, rest }
}

/** One event's data as JSON, or null where it is not. */
export const eventData = <T>(event: ISseEvent): T | null => {
    try {
        return JSON.parse(event.data) as T
    } catch {
        return null
    }
}
